import {SalesPerson, salesPersonsSchema} from "sharedlibs";
import {fetchBackend, fetchBackendChecked, getGETUrl} from "../utils/helperFunctions";
import Query from "../models/Query";

export async function getSalesPersonById(id: string): Promise<SalesPerson | undefined> {
  try {
    const salesPersonResponse = await fetchBackend(getGETUrl(`salesperson/${id}`, {}))
    return salesPersonsSchema.parse(salesPersonResponse[0])
  } catch (e) {
    console.error('Error while fetching from server, ', e)
    return undefined
  }
}

export async function getSalesPersons(query?: Query): Promise<SalesPerson[]> {
  try {
    const salesPersonsResponse = await fetchBackend(getGETUrl("salesperson/", query))
    if (Array.isArray(salesPersonsResponse)) {
      return salesPersonsResponse.map((elem) => {
        return salesPersonsSchema.parse(elem)
      })
    } else {
      throw new Error('Response is not of type JSON')
    }
  } catch (e) {
    throw new Error('Error while fetching from server, ' + e)
  }
}

// Both writes below deliberately let failures propagate rather than reporting them as `false`.
// Swallowing the error discarded the reason -- so the caller could not tell a rejected duplicate
// from a network outage, and (combined with fetchBackend ignoring the status code) a rejected save
// was reported to the user as a success.

export async function editSalesPerson(salesPerson: SalesPerson) {
  await fetchBackendChecked(getGETUrl(`salesperson/${salesPerson.id}`, {}), 'PUT', salesPerson)

  return true
}

export async function addSalesPerson(salesPerson: SalesPerson) {
  await fetchBackendChecked(getGETUrl("salesperson/", {}), 'POST', salesPerson)

  return true
}

export interface SalesPersonMapCustomer {
  name: string;
  city: string;
  postalCode: string;
  patientCount: number;
  tmzCount: number;
  extSensorsCount: number;
  implantCount: number;
  implantsByManufacturer: Record<string, string[]>;
  lat: number | null;
  lng: number | null;
}

export interface SalesPersonMapEntry {
  id: string;
  lastName: string;
  patientCount: number;
  tmzCount: number;
  extSensorsCount: number;
  implantCount: number;
  customers: SalesPersonMapCustomer[];
}

export interface SalesPersonMapData {
  date: string;
  salesPersons: SalesPersonMapEntry[];
  // 'current' means this past-date view fell back to current salesperson assignments because no
  // monthly snapshot existed for that month; the map surfaces a caveat in that case. Optional so an
  // older server response (without the field) is tolerated — treated as "no caveat".
  assignmentBasis?: 'live' | 'snapshot' | 'current';
}

export async function getSalesPersonMapData(date?: string): Promise<SalesPersonMapData> {
  const data = await fetchBackend(getGETUrl('salesperson/map-data', date ? {date} : {}));
  if (!data || !Array.isArray(data.salesPersons)) {
    throw new Error('Failed to load salesperson map data');
  }
  return data as SalesPersonMapData;
}

export async function deleteSalesPerson(id: string) {
  try {
    await fetchBackend(getGETUrl(`salesperson/${id}`, {}), 'DELETE')
  } catch (e) {
    console.error('Error while trying to delete sales person, ', e)
  }
}
